import { useEffect, useRef } from 'react'
import { marked } from 'marked'
import './markedChatConfig'
import Prism from 'prismjs'
// Import Prism theme - using VS Code dark theme style
import 'prismjs/themes/prism-tomorrow.css'
// Import language components
import 'prismjs/components/prism-markup' // HTML/XML
import 'prismjs/components/prism-markup-templating' // Required for PHP and templating languages
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-c'
import 'prismjs/components/prism-cpp'
import 'prismjs/components/prism-csharp'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-ruby'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-rust'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-markdown'

interface MarkdownMessageProps {
  content: string
  role: string
  className?: string
}

function fixMathDelimiters(text: string) {
  let t = text
  t = t.replace(/\[([^\]]*\\[^\]]*)\]/g, (_, expr) => `$$${expr}$$`)
  t = t.replace(/\(([^()]*\\[^()]*)\)/g, (_, expr) => `\\(${expr}\\)`)
  return t
}

export function MarkdownMessage({ content, role, className = '' }: MarkdownMessageProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!contentRef.current) return

    // Fix math delimiters
    const fixed = fixMathDelimiters(content)

    // Render markdown (marked will add language classes)
    const html = marked.parse(fixed)
    contentRef.current.innerHTML = typeof html === 'string' ? html : String(html)

    // Re-highlight code blocks after rendering
    if (contentRef.current) {
      contentRef.current.querySelectorAll('pre code').forEach((block) => {
        const codeElement = block as HTMLElement
        // Get language from class (marked adds 'language-{lang}' class)
        const classList = Array.from(codeElement.classList)
        const langClass = classList.find(cls => cls.startsWith('language-'))
        const language = langClass ? langClass.replace('language-', '') : 'plaintext'

        // Highlight if language is supported, otherwise use plaintext
        if (language && language !== 'plaintext' && Prism.languages[language]) {
          try {
            Prism.highlightElement(codeElement)
          } catch (e) {
            // If highlighting fails, fallback to plaintext
            console.warn('Failed to highlight code block with language:', language, e)
            try {
              Prism.highlightElement(codeElement, Prism.languages.plaintext || Prism.languages.markup, 'plaintext')
            } catch (fallbackError) {
              // If even plaintext fails, just keep the original code
              console.warn('Failed to highlight with plaintext fallback:', fallbackError)
            }
          }
        } else {
          // Re-highlight with appropriate language or fallback to plaintext
          try {
            Prism.highlightElement(codeElement, Prism.languages[language] || Prism.languages.plaintext || Prism.languages.markup, language || 'plaintext')
          } catch (e) {
            // If highlighting fails, keep original code
            console.warn('Failed to highlight code block:', e)
          }
        }
      })
    }

    // Add styling to code blocks
    contentRef.current.querySelectorAll('pre').forEach((pre) => {
      const element = pre as HTMLElement
      element.style.borderRadius = '8px'
      element.style.padding = '16px'
      element.style.overflowX = 'auto'
      element.style.margin = '12px 0'
    })

    // Add styling to inline code (not inside pre)
    contentRef.current.querySelectorAll('code').forEach((code) => {
      if (!code.parentElement?.tagName.match(/pre/i)) {
        const element = code as HTMLElement
        element.style.padding = '2px 6px'
        element.style.borderRadius = '4px'
        element.style.fontSize = '0.75em'
      }
    })

    // Add styling to tables
    contentRef.current.querySelectorAll('table').forEach((table) => {
      const element = table as HTMLElement
      element.style.borderCollapse = 'collapse'
      element.style.width = '100%'
      element.style.marginTop = '12px'
      element.style.marginBottom = '12px'
    })

    contentRef.current.querySelectorAll('th, td').forEach((cell) => {
      const element = cell as HTMLElement
      element.style.border = '1px solid'
      element.style.borderColor = role === 'user' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'
      element.style.padding = '8px 12px'
    })

    contentRef.current.querySelectorAll('th').forEach((th) => {
      const element = th as HTMLElement
      element.style.backgroundColor = role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
      element.style.fontWeight = '600'
    })

    // Add styling to lists
    contentRef.current.querySelectorAll('ul, ol').forEach((list) => {
      const element = list as HTMLElement
      element.style.marginTop = '8px'
      element.style.marginBottom = '8px'
      element.style.paddingLeft = '24px'
    })

    contentRef.current.querySelectorAll('li').forEach((li) => {
      const element = li as HTMLElement
      element.style.marginTop = '4px'
      element.style.marginBottom = '4px'
    })
  }, [content, role])

  return (
    <div
      ref={contentRef}
      className={`text-sm leading-relaxed prose prose-sm max-w-none ${className} ${
        role === 'user'
          ? 'prose-invert prose-p:text-white prose-headings:text-white prose-strong:text-white prose-code:text-white prose-a:text-indigo-200 prose-pre:bg-indigo-700/50'
          : 'prose-gray dark:prose-invert prose-p:text-gray-900 dark:prose-p:text-white prose-headings:text-gray-900 dark:prose-headings:text-white prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-pre:bg-gray-50 dark:prose-pre:bg-gray-800'
      }`}
      style={{
        wordBreak: 'break-word',
      }}
    />
  )
}

